/**
 * The item standard — the one place it is defined.
 *
 * "An item is a first-class citizen": the system makes sure it meets a standard,
 * so it "is self-evident, and can be compared and ranked fairly with other items
 * or priorities." These five questions are that standard, turned into something a
 * person can answer about one item. Draft for the owner and Harry Max to mark up:
 * docs/9-exploration/DRAFT-the-item-standard.md. Nothing here is canon yet.
 *
 * The questions work for anything an item can be: people, places, things,
 * activities, information, time. So nothing here reads effort, work type or a
 * success measure, which only make sense for an activity. (That is why #1049's
 * grade of measure, owner, estimate and reason is gone; the owner took it off on
 * Oct 15 as not what matters most.)
 *
 * It is not a second rule engine. What a workspace says every item needs comes
 * from its every-item rules (computeItemReadiness, canon "Completeness"), and a
 * gap there counts against the question it belongs to. Whether a value is there
 * for a track's criterion comes from computeContextualReadiness. What is added
 * here is only what the claim itself asks for.
 *
 * Rule-based on purpose: each answer says what the record holds, never how good
 * the words are. "Holds" is the settled word for a record (the wristband page).
 * Read at view time and never stored, like readiness (canon 04). Nothing is
 * blocked by it. The AI judgment waits for Harry's line.
 */

import type { Candidate, Item } from '../types/prioritization/lifecycle';
import { computeContextualReadiness, computeItemReadiness, type ItemReadinessIssue } from './itemReadiness';

export type ItemStandardQuestionId = 'what' | 'why' | 'compared' | 'context' | 'history';

/** The questions, in the order a person would ask them. */
export const ITEM_STANDARD_QUESTIONS: readonly { id: ItemStandardQuestionId; question: string; claim: string }[] = [
  { id: 'what', question: 'Could a stranger read this and say what it is?', claim: 'self-evident' },
  { id: 'why', question: 'Does it say why it matters, and why now?', claim: 'understandable and defensible; temporal relevance' },
  { id: 'compared', question: 'Does it carry the attributes and values it would be compared on?', claim: 'attributes, attribute values; compared and ranked fairly' },
  { id: 'context', question: 'Does it say what context it applies in?', claim: 'contextual relevance' },
  { id: 'history', question: 'Can its history be inspected?', claim: 'metadata; it can be inspected' },
];

/** One answer, read from the record. */
export interface ItemStandardAnswer {
  id: ItemStandardQuestionId;
  question: string;
  holds: boolean;
}

/** WORKING LINE for "sufficient", awaiting Harry Max's line. Nobody has ruled on it; his line replaces this one line and nothing else changes. */
export const SUFFICIENT = { words: 'Working line, not yet ruled on: an item is sufficient when every question holds.', isMet: (answers: readonly ItemStandardAnswer[]) => answers.every((a) => a.holds) };

/** Where on the item page a gap is filled in. The page says what each one opens. */
export type ItemStandardFill = 'description' | 'category' | 'why' | 'values' | 'context' | 'owner' | 'history';

/** What the page already holds about the item, beyond the item's own fields. Nothing new is read for this. */
export interface ItemStandardRecord {
  /** How the page words the item's category and altitude ("Commitment at altitude 3 · Feature"). Null when neither is set. */
  classification?: string | null;
  /** Attributes carrying a value, as the page shows them. Custom fields are descriptive and not counted. */
  values: { label: string; value: string }[];
  /** The criteria the tracks holding this item compare on. Empty: no evaluation has chosen any yet. */
  criteriaKeys: string[];
  attributeLabel: (key: string) => string;
  /** Promises resting on the item: commitments and assignments. `due` is already worded for display. */
  promises: { title: string; due: string | null }[];
  /** The plan of record holding the item, if any. `since` is already worded for display. */
  plan?: { name: string; since: string | null } | null;
  /** When a deferred item comes up for review, already worded for display. */
  reviewOn?: string | null;
  /** Objectives and goals it supports. */
  supports: string[];
  /** The title of its parent item. */
  parent?: string | null;
  /** The product it is placed under. */
  product?: string | null;
  /** Cycles and sessions that hold it. */
  evaluations: string[];
  /** How many other items it is linked to or grouped with. */
  relatedCount: number;
  /** Versions recorded; null when they could not be read. */
  versionCount: number | null;
  /** One sentence on who or what made it. */
  origin: string;
  /** False when the record does not say who or what made it. */
  originKnown: boolean;
  /** Entries in "Who did what" and in its lineage. */
  eventCount: number;
}

export interface ItemStandardReading {
  /** Every question, in order, with what the record holds for it. */
  answers: (ItemStandardAnswer & {
    /** What the record holds, one sentence each. */
    held: string[];
    /** What it does not hold, one sentence each. */
    missing: string[];
    /** Where those gaps are filled in. The history question always says where to look. */
    fills: ItemStandardFill[];
  })[];
  /** True when the item meets the working line. */
  sufficient: boolean;
}

/** Which question each every-item rule belongs to. Anything unlisted says what the item is. */
const RULE_QUESTION: Record<string, ItemStandardQuestionId> = {
  rationale: 'why',
  ownerId: 'context',
  parentItemId: 'context',
  beneficiary: 'context',
  strategicBucket: 'context',
};

/** Which every-item rules the item page has a place to fill in. */
const RULE_FILL: Record<string, ItemStandardFill> = {
  itemCategory: 'category',
  altitudeLevel: 'category',
  ownerId: 'owner',
};

const said = (text: string | null | undefined): string | null => {
  const t = text?.trim();
  return t ? t : null;
};

const normalized = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

const LONGEST_QUOTE = 160;
const quote = (text: string) => `“${text.length > LONGEST_QUOTE ? `${text.slice(0, LONGEST_QUOTE).trimEnd()}…` : text}”`;

const MOST_LISTED = 3;
function listed(names: string[]): string {
  const shown = names.slice(0, MOST_LISTED).join(', ');
  return names.length > MOST_LISTED ? `${shown}, and ${names.length - MOST_LISTED} more` : shown;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function questionOf(issue: ItemReadinessIssue): ItemStandardQuestionId {
  if (issue.fieldKey.startsWith('attr:')) return 'compared';
  return RULE_QUESTION[issue.fieldKey] ?? 'what';
}

function fillOf(issue: ItemReadinessIssue): ItemStandardFill | null {
  if (issue.fieldKey.startsWith('attr:')) return 'values';
  return RULE_FILL[issue.fieldKey] ?? null;
}

/** The gaps the workspace's every-item rules (Warning or Hard Stop) put against one question. */
function ruleGaps(issues: ItemReadinessIssue[], id: ItemStandardQuestionId) {
  const gaps = issues.filter((issue) => questionOf(issue) === id);
  return {
    missing: gaps.map((issue) => issue.missingText),
    fills: gaps.map(fillOf).filter((f): f is ItemStandardFill => f !== null),
  };
}

/** Reads the standard off one item. `record` is what the page already holds about it. */
export function readItemStandard(item: Item, record: ItemStandardRecord): ItemStandardReading {
  const candidate = item as unknown as Candidate;
  const rules = computeItemReadiness(candidate);
  const issues = [...rules.required, ...rules.recommended];
  const answers = new Map<ItemStandardQuestionId, { held: string[]; missing: string[]; fills: ItemStandardFill[] }>();

  // ── What it is: words a stranger can read, beyond the title on a list. ──
  {
    const { missing, fills } = ruleGaps(issues, 'what');
    const description = said(item.description);
    const repeatsTitle = description !== null && normalized(description) === normalized(item.title ?? '');
    const held: string[] = [];
    if (description && !repeatsTitle) held.push(quote(description));
    else {
      missing.unshift(repeatsTitle ? 'Its description only repeats the title.' : 'No description: only a title.');
      fills.unshift('description');
    }
    if (record.classification) held.push(`${record.classification}.`);
    answers.set('what', { held, missing, fills });
  }

  // ── Why it matters, and why now. An item has no field of its own for time, and the
  //    altitude answer stored as timeHorizon is reversibility (lib/altitudeQuestions.ts), so
  //    temporal relevance is read from the box that asks "why this, and why now?" and from
  //    the dates that rest on the item. ──
  {
    const { missing, fills } = ruleGaps(issues, 'why');
    const reason = said(item.whyNow);
    const outcome = said(item.desiredOutcome);
    const dated = [
      ...record.promises.filter((p) => p.due).map((p) => `Promised by ${p.due}: ${p.title}.`),
      ...(record.plan ? [`Of record in ${record.plan.name}${record.plan.since ? ` since ${record.plan.since}` : ''}.`] : []),
      ...(record.reviewOn ? [`Deferred, up for review on ${record.reviewOn}.`] : []),
    ];
    const held = [...(reason ? [quote(reason)] : []), ...(outcome ? [`Desired outcome: ${quote(outcome)}`] : []), ...dated];
    if (!reason && (!outcome || dated.length === 0)) {
      missing.unshift(outcome ? 'It says why, but not why now.' : dated.length ? 'Nothing says why it matters.' : 'Nothing says why it matters, or why now.');
      fills.unshift('why');
    }
    answers.set('why', { held, missing, fills });
  }

  // ── What it would be compared on: at least one attribute with a value, and a value on
  //    every criterion a track holding it compares on. Whether it is comparable is known
  //    only inside a comparison (canon 02 §8), so this says what it carries, no more. ──
  {
    const { missing, fills } = ruleGaps(issues, 'compared');
    const held: string[] = [];
    const unvalued = computeContextualReadiness(candidate, { requiredAttributeKeys: record.criteriaKeys }).attributeIssues
      .map((issue) => issue.attributeKey)
      .filter((key): key is string => !!key);
    for (const key of unvalued) {
      const label = record.attributeLabel(key);
      // A workspace rule and a track can name the same attribute: say it once.
      if (!missing.includes(`${label} has no value.`)) missing.push(`${label} has no value, and a track holding this item compares on it.`);
      fills.push('values');
    }
    if (record.values.length > 0) {
      const shown = record.values.slice(0, MOST_LISTED).map((v) => `${v.label} ${v.value}`);
      const more = record.values.length - shown.length;
      held.push(`${shown.join(' · ')}${more > 0 ? ` · and ${more} more` : ''}.`);
      if (record.criteriaKeys.length === 0) held.push('No evaluation has chosen its criteria yet, so how it compares is known only once it is in one.');
      else if (unvalued.length === 0) held.push('Every criterion its tracks compare on has a value.');
    } else if (missing.length === 0) {
      missing.push('No attribute carries a value.');
      fills.push('values');
    }
    answers.set('compared', { held, missing, fills });
  }

  // ── The context it applies in: anything that ties it to something outside itself. ──
  {
    const { missing, fills } = ruleGaps(issues, 'context');
    const beneficiary = said(item.beneficiary);
    const held = [
      ...(record.supports.length ? [`Supports ${listed(record.supports)}.`] : []),
      ...(record.parent ? [`Part of ${record.parent}.`] : []),
      ...(record.product ? [`Placed under ${record.product}.`] : []),
      ...(beneficiary ? [`For ${beneficiary}.`] : []),
      ...(item.strategicBucket ? [`Strategic bucket: ${item.strategicBucket}.`] : []),
      ...(record.promises.length ? [`${plural(record.promises.length, 'promise')} rest${record.promises.length === 1 ? 's' : ''} on it.`] : []),
      ...(record.relatedCount ? [`Linked with ${plural(record.relatedCount, 'other item')}.`] : []),
      ...(record.evaluations.length ? [`Weighed in ${listed(record.evaluations)}.`] : []),
    ];
    if (held.length === 0) {
      missing.unshift('Nothing ties it to anything outside itself.');
      fills.unshift('context');
    }
    answers.set('context', { held, missing, fills });
  }

  // ── Its history: where it came from, and what has been recorded since. ──
  {
    const held: string[] = [];
    const missing: string[] = [];
    if (record.versionCount === null) missing.push('Its versions could not be read.');
    else if (record.versionCount === 0) missing.push('No version of what it says has been recorded.');
    else held.push(`${plural(record.versionCount, 'version')} of what it says, and ${plural(record.eventCount, 'recorded event')}.`);
    if (record.originKnown) held.push(record.origin);
    else missing.push('The record does not say who or what made it.');
    answers.set('history', { held, missing, fills: ['history'] });
  }

  const reading = ITEM_STANDARD_QUESTIONS.map(({ id, question }) => {
    const answer = answers.get(id)!;
    return { id, question, ...answer, fills: [...new Set(answer.fills)], holds: answer.missing.length === 0 };
  });
  return { answers: reading, sufficient: SUFFICIENT.isMet(reading) };
}
